/**
 * Feature-flag and configuration helpers for the Nota Admin Login integration.
 *
 * Reads CRA build-time env vars (`REACT_APP_*`). Off by default — when the
 * flag is unset / "false", every consumer must behave exactly like the legacy
 * local-only auth path.
 *
 * Naming note: the original task spec used `VITE_*`. This codebase is CRA
 * (react-scripts), where only `REACT_APP_*` vars are exposed to the bundle —
 * `VITE_*` would be silently dropped at build time. The names here use the
 * CRA convention; semantics are identical to the spec.
 */

const envBool = (v: string | undefined): boolean => {
  if (!v) return false;
  return ['true', '1', 'yes', 'on'].includes(v.trim().toLowerCase());
};

/** Master kill-switch for the parallel Nota auth flow. Default: false. */
export const isNotaAuthEnabled = (): boolean =>
  envBool(process.env.REACT_APP_NOTA_AUTH_ENABLED);

/**
 * Resolve the Nota Admin API base URL — the host that exposes
 * `/admin/login`, `/admin/refresh-token`, `/admin/logout`. Returns null when
 * unset; callers must treat that as "Nota auth not configured" and fall back
 * to local even if the flag is on.
 */
export const getNotaAdminApiBaseUrl = (): string | null => {
  const raw = process.env.REACT_APP_NOTA_ADMIN_API_BASE_URL;
  if (!raw) return null;
  return raw.trim().replace(/\/+$/, '');
};

/**
 * Per-the-Nota-guide app identifier sent on every login/refresh call to bind
 * the resulting refresh-token lifetime. ADMIN_WEB = 24h. Override with
 * REACT_APP_NOTA_APP_ID if Nota assigns this app a dedicated id.
 */
export const getNotaAppId = (): string =>
  process.env.REACT_APP_NOTA_APP_ID?.trim() || 'ADMIN_WEB';

/**
 * Returns true when both the flag is on AND a base URL is configured. Use
 * this at every dispatch point — the flag alone is not sufficient because a
 * misconfigured environment (flag on, URL missing) must fall back rather
 * than crash.
 */
export const isNotaAuthReady = (): boolean =>
  isNotaAuthEnabled() && getNotaAdminApiBaseUrl() !== null;
